import React, { useMemo, useRef } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { SearchReservationTypeWhenCheckedIn, SearchReservationTypeWhenNonCheckIn } from '@/models/reservation'
import { FrontTabletCheckbox } from '@/components/molecules/front-tablet/input/checkbox'
import { maxBy, minBy } from 'lodash'
import { FrontTabletRadioButton } from '@/components/molecules/front-tablet/input/radio-button'
import { RoomOptionDisplayType } from '@/models/self-checkin/setting'
import { FrontTabletFontSize } from '@/constants/front-tablet-typography'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

type Props = {
  checkinDate: Dayjs
  nights: string | number
  onClick(): void
  displayType: RoomOptionDisplayType
  isSelected: boolean
  reservations: SearchReservationTypeWhenNonCheckIn[] | SearchReservationTypeWhenCheckedIn[]
}

const Card: React.FC<Props> = props => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  return (
    <div css={[cardStyle, fontStyle]} onClick={() => props.onClick()} className={`${props.isSelected ? '-selected' : ''}`}>
      <div className="header-block">
        <div className="block-inner flex-inline">
          <div className="flex-inline">
            {props.children}
            <div className={`text-title ${props.isSelected ? '-selected' : ''}`}>
              {props.checkinDate.format(t('SelfCheckIn.MM-DD(ddd)'))}
              {'\u00a0'}
              {`${props.nights}${t('Night')}`}
            </div>
          </div>
          <img className="arrow-icon" src={require('@/static/images/room_manager_arrow.svg')} />
        </div>
      </div>
      {props.reservations.map(reservation => (
        <React.Fragment key={reservation.reservationId}>
          <div className="body-block">
            <div className="block-inner">
              <div className="plan-info">{lang === 'ja' ? reservation.plan : reservation.planEn}</div>
              <div className="flex-inline">
                <span>{t('Reservation holder')}</span>：<div>{reservation.userName}</div>
              </div>
              {props.displayType === RoomOptionDisplayType.GuestName && (
                <div className="flex-inline">
                  <span>{t('Confirmed guest')}</span>：<div>{reservation.guestName}</div>
                </div>
              )}
            </div>
          </div>
          <div className="footer-block">
            <div className="block-inner">
              <div className="flex-inline text-footer">
                <span>{t('Reservation ID')}</span>：<div className="text-truncate">{reservation.reservationId}</div>
              </div>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

type CheckinCardProps = {
  reservations: SearchReservationTypeWhenCheckedIn[]
  value: string | undefined
  displayType: RoomOptionDisplayType
  onClick(): void
}

export const CheckinCard: React.FC<CheckinCardProps> = props => {
  const checkinId = useRef(props.reservations[0].checkinId)
  const checkinDate = useRef(minBy(props.reservations, 'checkinDate')?.checkinDate)
  const chekcoutDate = useRef(maxBy(props.reservations, 'checkoutDate')?.checkoutDate)
  const nights = useRef(dayjs(chekcoutDate.current).diff(dayjs(checkinDate.current), 'day'))

  return (
    <Card
      checkinDate={dayjs(checkinDate.current)}
      nights={nights.current}
      onClick={() => checkinId.current !== props.value && props.onClick()}
      isSelected={checkinId.current === props.value}
      reservations={props.reservations}
      displayType={props.displayType}
    >
      <div onClick={e => e.stopPropagation()}>
        <FrontTabletRadioButton
          id={`radio-${checkinId.current}`}
          value={checkinId.current}
          name="checkin-id"
          formValue={props.value}
          onChange={() => props.onClick()}
          radioSizeType="normal"
          radioLabelSizeType="small"
        />
      </div>
    </Card>
  )
}

type ReservationCardProps = {
  reservation: SearchReservationTypeWhenNonCheckIn
  value: SearchReservationTypeWhenNonCheckIn[]
  displayType: RoomOptionDisplayType
  onClick(): void
}

export const ReservationCard: React.FC<ReservationCardProps> = props => {
  const isSelected = useMemo(
    () => props.value.some(r => r.reservationId === props.reservation.reservationId),
    [props.value, props.reservation],
  )

  return (
    <Card
      checkinDate={dayjs(props.reservation.checkinDate)}
      nights={props.reservation.nights}
      onClick={props.onClick}
      isSelected={isSelected}
      reservations={[props.reservation]}
      displayType={props.displayType}
    >
      <FrontTabletCheckbox value={isSelected} />
    </Card>
  )
}

type ReservationWithNoRoomCardProps = {
  reservation: SearchReservationTypeWhenNonCheckIn
  reservationGroups: SearchReservationTypeWhenNonCheckIn[]
  value: SearchReservationTypeWhenNonCheckIn[]
  displayType: RoomOptionDisplayType
  onClick(): void
}
export const ReservationWithNoRoomCard: React.FC<ReservationWithNoRoomCardProps> = props => {
  const isSelected = useMemo(
    () => props.value.some(r => r.reservationId === props.reservation.reservationId),
    [props.value, props.reservation],
  )
  const checkinDate = useRef(minBy(props.reservationGroups, 'checkinDate')?.checkinDate)
  const chekcoutDate = useRef(maxBy(props.reservationGroups, 'checkoutDate')?.checkoutDate)
  const nights = useRef(dayjs(chekcoutDate.current).diff(dayjs(checkinDate.current), 'day'))

  return (
    <Card
      checkinDate={dayjs(checkinDate.current)}
      nights={nights.current}
      onClick={props.onClick}
      isSelected={isSelected}
      reservations={props.reservationGroups}
      displayType={props.displayType}
    >
      <FrontTabletCheckbox value={isSelected} />
    </Card>
  )
}

const fontStyle = css(
  {
    lineHeight: 1.5,
    fontSize: FrontTabletFontSize.formLabel,
    fontWeight: 500,
    letterSpacing: '1px',
    color: '#272727',
    '.text-title': {
      fontSize: FrontTabletFontSize.radioLabel,
      fontWeight: 'bold',
      lineHeight: '33px',
      letterSpacing: '1.2px',
      '&.-selected': { color: '#f2a40b' },
    },
    '.text-footer': { fontSize: 18, fontWeight: 500, lineHeight: '25px', letterSpacing: '0.9px', color: '#676767' },
    '.text-truncate': {
      minWidth: 0,
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      fontSize: 24,
      lineHeight: '33px',
      letterSpacing: '1.2px',
      '.text-title': { fontSize: 28, lineHeight: '38px', letterSpacing: '1.4px' },
      '.text-footer': { fontSize: 22, lineHeight: '30px', letterSpacing: '1.1px' },
    },
  }),
)

const cardStyle = css(
  {
    borderRadius: 10,
    background: '#fff',
    marginTop: 16,
    boxShadow: '0 0 0 1px #cccccc',
    height: 'fit-content',
    '.flex-inline': {
      display: 'flex',
      alignItems: 'center',
      '>span': { whiteSpace: 'nowrap' },
    },
    '.plan-info': {
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': '3',
      '-webkit-box-orient': 'vertical',
      wordWrap: 'break-word',
    },
    '&.-selected': {
      boxShadow: '0 0 0 2px #f2a40b',
      backgroundColor: '#fffde3',
    },
    '.header-block': {
      '.block-inner': {
        padding: '15px 24px',
        justifyContent: 'space-between',
        '.arrow-icon': { width: 18, height: 29 },
      },
    },
    '.body-block': {
      borderTop: '1px solid #CCCCCC',
      '.block-inner': {
        padding: '15px 24px 16px',
        '*': { lineHeight: '27px', letterSpacing: '1px' },
        '>div': { paddingTop: 0, lineHeight: '27px', letterSpacing: '1px' },
      },
    },
    '.footer-block': {
      borderTop: '1px solid #CCCCCC',
      '.block-inner': { padding: '7px 24px 5px' },
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      '.header-block .block-inner': { padding: '24px' },
      '.body-block .block-inner': {
        padding: '24px',
        '*': { lineHeight: '33px', letterSpacing: '1.2px' },
        '>div': { lineHeight: '33px', letterSpacing: '1.2px' },
      },
      '.footer-block .block-inner': { padding: '16px 24px' },
    },
  }),
)
